import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import React from 'react';
import { CodeContent } from './content/CodeContent';
import { DetailsContent } from './content/DetailsContent';
import { FailureContent } from './content/FailureContent';
import { PrettyContent } from './content/PrettyContent';
import { cn } from '@/lib/utils';

interface Tab {
  id: string;
  label: string;
  content: any;
  type: 'code' | 'details' | 'custom' | 'flow' | 'failure';
  disabled?: boolean;
  status?: 'completed' | 'failed';
}

interface ContentTabsProps {
  tabs: Tab[];
  value?: string;
  onTabChange?: (tabId: string) => void;
}

export const ContentTabs: React.FC<ContentTabsProps> = ({ tabs, value, onTabChange }) => {
  const [mode, setMode] = React.useState<'pretty' | 'raw'>('pretty');

  const visibleTabs = tabs.filter((tab) => {
    if (tab.id === 'details') {
      return tab.content && (tab.content.description || tab.content.flowChart);
    }
    return true;
  });

  const safeStringify = (content: any): string => {
    try {
      if (typeof content === 'string') return content;
      if (content === null || content === undefined) return '';
      if (
        React.isValidElement(content) ||
        (typeof content === 'object' &&
          (content.$$typeof || content._reactFiber || content.__reactFiber))
      ) {
        return 'React component content';
      }
      return JSON.stringify(content, null, 2);
    } catch (error) {
      console.error('Error stringifying content:', error);
      return 'Error displaying content';
    }
  };

  const renderContent = (tab: Tab) => {
    if (tab.type === 'code') {
      const content = safeStringify(tab.content);
      if (mode === 'pretty') {
        return <PrettyContent content={content} />;
      } else {
        return <CodeContent content={content} />;
      }
    }

    if (tab.type === 'failure') {
      return (
        <FailureContent
          message={tab.content.message}
          type={tab.content.type}
          retryable={tab.content.retryable}
          stackTrace={tab.content.stackTrace}
        />
      );
    }

    if (tab.type === 'details') {
      return (
        <DetailsContent description={tab.content.description} flowChart={tab.content.flowChart} />
      );
    }

    return tab.content;
  };

  const currentTab = visibleTabs.find((tab) => tab.id === (value || visibleTabs[0].id));
  const showModeToggle = currentTab?.type === 'code';

  return (
    <Tabs
      value={value || visibleTabs[0].id}
      onValueChange={onTabChange}
      className="w-full h-full min-w-0 flex flex-col"
    >
      {/* Tab Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
        <TabsList className="h-9 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-lg">
          {visibleTabs.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              disabled={tab.disabled}
              className={cn(
                'px-4 py-1.5 text-sm font-medium rounded-md transition-all',
                'data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700',
                'data-[state=active]:text-zinc-900 dark:data-[state=active]:text-zinc-100',
                'data-[state=active]:shadow-sm',
                tab.disabled && 'opacity-50 cursor-not-allowed',
                tab.status === 'failed' &&
                  'text-red-600 dark:text-red-400 data-[state=active]:text-red-600 dark:data-[state=active]:text-red-400',
                tab.status === 'completed' &&
                  'text-emerald-600 dark:text-emerald-400 data-[state=active]:text-emerald-600 dark:data-[state=active]:text-emerald-400'
              )}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {showModeToggle && (
          <ToggleGroup
            type="single"
            value={mode}
            onValueChange={(value) => value && setMode(value as 'pretty' | 'raw')}
            className="bg-zinc-100 dark:bg-zinc-800 p-1 rounded-lg"
          >
            <ToggleGroupItem
              value="pretty"
              size="sm"
              className="h-7 px-3 text-xs font-medium rounded-md data-[state=on]:bg-white dark:data-[state=on]:bg-zinc-700 data-[state=on]:shadow-sm"
            >
              Pretty
            </ToggleGroupItem>
            <ToggleGroupItem
              value="raw"
              size="sm"
              className="h-7 px-3 text-xs font-medium rounded-md data-[state=on]:bg-white dark:data-[state=on]:bg-zinc-700 data-[state=on]:shadow-sm"
            >
              Raw
            </ToggleGroupItem>
          </ToggleGroup>
        )}
      </div>

      {/* Tab Content */}
      {visibleTabs.map((tab) => (
        <TabsContent
          key={tab.id}
          value={tab.id}
          className="flex-1 overflow-hidden m-0 data-[state=active]:flex data-[state=active]:flex-col min-h-0 min-w-0"
        >
          <div
            className={cn(
              'h-full w-full min-w-0 relative',
              tab.type === 'code' ? 'overflow-hidden' : 'overflow-auto',
              tab.type === 'code' && mode === 'raw' && 'bg-[#1E1E1E]'
            )}
          >
            {renderContent(tab)}
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
};
